import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { programs, supervision } from '@cwos/api-client';
import {
  Pill,
  Refused,
  SuccessEmpty,
  TableCard,
  useTabListKeys,
  type Column,
  ScopeNote,
} from '@cwos/ui-admin';
import { usePermissions } from '../permissions.js';
import { figureNote, figurePhase, figureText, type FigurePhase } from '../figureState.js';
import { scopeNoteFor, statedPopulation } from '../readScope.js';
import { listPhase } from '../listState.js';
import { ListState } from '../ListStates.js';
import { pickerState, type PickerState } from '../pickerState.js';
import { useNav } from '../navigation.js';
import { localDayString } from '../../lib/timeFormat.js';
import { SettingsCard, SettingsField, SettingsHeader } from '../settings/common.js';
import {
  CADENCE_LABELS,
  EMPTY_REQUIREMENT_DRAFT,
  STATUS_TABS,
  draftFromRequirement,
  formatCalendarDate,
  messageOf,
  minutesLabel,
  requirementPayload,
  statusPill,
  validateRequirementDraft,
  type RequirementDraft,
} from './supervisionLogic.js';

/**
 * Supervision — the workforce compliance list, and (for `supervision:manage`)
 * what the organization requires.
 *
 * STATUS IS NEVER COMPUTED HERE. Every row's status comes straight off
 * `GET /supervision/workers`, already derived server-side from the requirement
 * in force and the sessions recorded — this screen only decides how it reads.
 *
 * SCOPING IS SERVER-SIDE. Without `supervision:manage`, the API narrows the
 * workforce list to the caller and the people actually assigned to them via
 * `worker.supervisor_id` (never by role name) — this screen renders whatever
 * comes back, and does not attempt to widen or re-narrow it.
 */
export function SupervisionCenter() {
  const qc = useQueryClient();
  const { has } = usePermissions();
  const navigate = useNav();
  const canManage = has('supervision:manage');

  const [tab, setTab] = useState<(typeof STATUS_TABS)[number]['key']>('all');
  const statusTabs = useTabListKeys<HTMLDivElement>();
  const [showRetired, setShowRetired] = useState(false);
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [draft, setDraft] = useState<RequirementDraft>(EMPTY_REQUIREMENT_DRAFT);
  const [formError, setFormError] = useState<string | null>(null);
  const [confirmRetireId, setConfirmRetireId] = useState<string | null>(null);

  const overview = useQuery({
    queryKey: ['supervision', 'overview'],
    queryFn: () => supervision.overview(),
  });

  const list = useQuery({
    queryKey: ['supervision', 'workers', tab],
    queryFn: () => supervision.workers({ status: tab === 'all' ? undefined : tab, limit: 500 }),
  });

  const requirements = useQuery({
    queryKey: ['supervision', 'requirements', showRetired],
    queryFn: () => supervision.requirements({ includeRetired: showRetired }),
    enabled: canManage,
  });

  // Only fetched while a requirement form is open, and only for a manager —
  // an org with no reason to see programs never pays for the request.
  const programOptions = useQuery({
    queryKey: ['programs', 'options'],
    queryFn: () => programs.list({ limit: 200 }),
    enabled: canManage && (creating || editingId !== null),
  });

  // Local calendar day — `toISOString().slice(0,10)` is the UTC date, which
  // rolled to tomorrow every evening and made "effective today" read as
  // "in the past".
  const today = localDayString(new Date());

  function resetForm() {
    setCreating(false);
    setEditingId(null);
    setDraft(EMPTY_REQUIREMENT_DRAFT);
    setFormError(null);
  }

  const create = useMutation({
    mutationFn: () => supervision.createRequirement(requirementPayload(draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const amend = useMutation({
    mutationFn: (id: string) => supervision.amendRequirement(id, requirementPayload(draft)),
    onSuccess: () => {
      resetForm();
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const retire = useMutation({
    mutationFn: (id: string) => supervision.retireRequirement(id),
    onSuccess: () => {
      setConfirmRetireId(null);
      void qc.invalidateQueries({ queryKey: ['supervision'] });
    },
    onError: (err: unknown) => setFormError(messageOf(err)),
  });

  const errors = validateRequirementDraft(draft, today);
  const canSubmit = Object.keys(errors).length === 0;

  /**
   * WHOSE COMPLIANCE THE SIX NUMBERS BELOW COUNT — TAKEN FROM THE SERVER.
   *
   * `GET /supervision/overview` summarizes `scopeComplianceRows`, so without
   * `supervision:manage` every figure on this card is over the reader and their
   * own direct reports. It now SAYS SO: `SupervisionOverview.scope` is resolved
   * by `overviewScope` (supervision/service.ts:575) — the same predicate that
   * scoped the rows being counted — and it arrives with the figures it
   * describes, so there is no window in which the numbers are on screen and the
   * frame is unknown.
   *
   * `noSupervisor` is the instance the withholding contract names as the worst
   * in the API: rendered bare, `0` asserts that everybody in the organization
   * has a supervisor.
   *
   * THIS USED TO BE `supervisionPopulation(perms)`, a client-side mirror of that
   * predicate built on `usePermissions().has` — which reads `/auth/me`'s
   * FLATTENED key list, with `scopeType`/`scopeId` already discarded (F46). A
   * `supervision:manage` devolved to ONE PROGRAM is `true` in that list and
   * `false` to the server's `can()`, so the mirror answered ORGANIZATION and
   * suppressed the scope note over a payload the server had framed as a
   * caseload — putting `noSupervisor: 0` back in front of a reader as a claim
   * about the whole organization. The mirror could not be fixed where it stood:
   * the client is not given the fields the decision is made from. The response
   * is, and `readScope.ts` always said to delete the mirror when it arrived.
   */
  const population = statedPopulation(overview.data?.scope, 'you and the people you supervise');

  /** One figure, in whichever of the five states it is actually in. */
  const stat = (value: number | undefined): FigurePhase =>
    figurePhase(overview, value ?? null, population);
  const statNote = figureNote(stat(overview.data?.overdue));
  const scopeNote = scopeNoteFor(population);

  const columns: Column<supervision.WorkerComplianceView>[] = [
    { key: 'worker', header: 'Worker', render: (r) => r.displayName },
    {
      key: 'supervisor',
      header: 'Supervisor',
      render: (r) => r.supervisorName ?? <span className="rn-muted">Unassigned</span>,
    },
    {
      key: 'status',
      header: 'Status',
      render: (r) => {
        const p = statusPill(r);
        return (
          <Pill variant={p.variant} dot>
            {p.label}
          </Pill>
        );
      },
    },
    {
      key: 'requirement',
      header: 'Requirement',
      render: (r) => r.requirementName ?? <span className="rn-muted">None applies</span>,
    },
    {
      key: 'minutes',
      header: 'This period',
      render: (r) => minutesLabel(r.minutesRequired, r.minutesRecorded),
    },
    {
      key: 'due',
      header: 'Next due / missed since',
      render: (r) =>
        r.missedPeriodStart
          ? `Missed ${formatCalendarDate(r.missedPeriodStart)}`
          : formatCalendarDate(r.nextDueOn),
    },
  ];

  return (
    <div className="rn-col" style={{ maxWidth: 1080 }}>
      <SettingsHeader
        title="Supervision"
        blurb="Who your organization requires supervision for, whether this period's requirement has been met, and who is overdue."
      />

      <SettingsCard>
        <div className="cmp-stats">
          <Stat label="Overdue" phase={stat(overview.data?.overdue)} />
          <Stat label="No supervisor" phase={stat(overview.data?.noSupervisor)} />
          <Stat label="Due soon" phase={stat(overview.data?.dueSoon)} />
          <Stat label="Due" phase={stat(overview.data?.due)} />
          <Stat label="Current" phase={stat(overview.data?.current)} />
          {/* NOT FOLDED INTO "Not applicable", and not omitted. Omitting it
              would leave the six figures below the total with nothing naming
              the difference, which reads as a rounding error rather than as
              people nobody can answer for. */}
          <Stat label="Cannot be determined" phase={stat(overview.data?.insufficientContext)} />
          <Stat label="Not applicable" phase={stat(overview.data?.notApplicable)} />
        </div>
        {/* One sentence for the whole row rather than six identical ones: the
            population, said where the numbers are. The ScopeNote further down
            covers the list, and a reader who never scrolls to it was taking
            these six as the organization's. */}
        {statNote !== null ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)', fontSize: 13 }}>
            {statNote}
          </p>
        ) : null}
        {overview.data ? (
          <p className="rn-muted" style={{ marginTop: 'var(--space-8)', fontSize: 13 }}>
            Times shown in {overview.data.timeZone}, your organization's timezone.
          </p>
        ) : null}
      </SettingsCard>

      <SettingsCard>
        {/* A tab strip owes ids, a panel and arrow keys. This one declared the
            role and delivered none of them. */}
        <div role="tablist" aria-label="Supervision status" className="cmp-tabs" {...statusTabs}>
          {STATUS_TABS.map((t) => (
            <button
              key={t.key}
              type="button"
              role="tab"
              id={`supervision-tab-${t.key}`}
              aria-controls="supervision-panel"
              aria-selected={tab === t.key}
              className="rn-tab-btn"
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* `GET /supervision/workers` narrows to the caller and the people whose
            `worker.supervisor_id` is them, and answers 200 with a short list —
            not a 403. So an empty result is indistinguishable, ON THE WIRE, from
            an organisation that has configured nothing, and this note is what
            distinguishes them.

            The comment here used to say "what the CLIENT does know is its own
            permission set". It knew a FLATTENED version of it, which is not the
            same thing and was the defect: see the tiles above. The frame is now
            the server's, off the overview payload, resolved by the same
            `overviewScope` predicate that scoped this list.

            KNOWN GAP: `GET /supervision/workers` states its own scope on the list
            envelope now too, which is the better source for a note beside the
            list. It is not yet declared in `@cwos/api-client`'s
            `supervision.ts` (another lane is adding it). Both are resolved by
            the same function for the same principal, so they cannot currently
            differ — but when the type lands, this should read the list's.

            It says what is being shown, never how much is hidden — that number
            is not knowable here, and inventing it would be the same error
            pointing the other way. Rendered only once the overview has arrived:
            before that the frame is `unknown`, and flashing "we could not check
            how far that is" on every visit says nothing useful about figures
            that are themselves still showing "…". */}
        {overview.data !== undefined && scopeNote !== null ? <ScopeNote scope={scopeNote} /> : null}
        <div id="supervision-panel" role="tabpanel" aria-labelledby={`supervision-tab-${tab}`}>
          <ListState
            phase={listPhase(list, list.data === undefined ? undefined : list.data.workers.length)}
            surface="table"
            rows={6}
            failedMessage="We couldn’t load the workforce list just now. Nobody has been found compliant or non-compliant by this."
            onRetry={() => void list.refetch()}
            refused={
              <Refused
                heading="Supervision compliance is not shown to you"
                message="Your account does not have access to this list. It is not an organization with nobody under supervision — it is a list you have not been given."
              />
            }
            empty={
              <SuccessEmpty
                heading={
                  list.data?.total === 0 && tab === 'all'
                    ? 'Nothing here yet'
                    : 'Nothing on this view'
                }
                message={
                  list.data?.total === 0 && tab === 'all'
                    ? 'No supervision requirements have been configured, or no workers are in scope yet.'
                    : 'No workers match this view right now.'
                }
              />
            }
          >
            {list.data ? (
              <TableCard
                columns={columns}
                rows={list.data.workers}
                rowKey={(r) => r.workerId}
                onRowOpen={(r) => navigate(`/workforce/supervision/${r.workerId}`)}
                caption="Workforce supervision status"
              />
            ) : null}
          </ListState>
        </div>
      </SettingsCard>

      {canManage ? (
        <SettingsCard>
          <div className="rn-card-head">
            <h2 className="rn-card-title">Requirements</h2>
            <div style={{ display: 'flex', gap: 'var(--space-8)' }}>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => setShowRetired((v) => !v)}
              >
                {showRetired ? 'Hide retired' : 'Show retired'}
              </button>
              <button
                type="button"
                className="rn-btn-quiet"
                onClick={() => {
                  if (creating) {
                    resetForm();
                  } else {
                    setCreating(true);
                    setEditingId(null);
                    setDraft(EMPTY_REQUIREMENT_DRAFT);
                    setFormError(null);
                  }
                }}
                aria-expanded={creating}
              >
                {creating ? 'Cancel' : 'New requirement'}
              </button>
            </div>
          </div>

          {creating || editingId ? (
            <RequirementForm
              draft={draft}
              setDraft={setDraft}
              errors={errors}
              programOptions={programOptions.data?.programs ?? []}
              programPicker={pickerState(
                listPhase(programOptions, programOptions.data?.programs.length),
                'programs',
                'Any program',
              )}
              isAmend={editingId !== null}
              submitting={create.isPending || amend.isPending}
              formError={formError}
              canSubmit={canSubmit}
              onCancel={resetForm}
              onSubmit={() => {
                if (!canSubmit) return;
                if (editingId) amend.mutate(editingId);
                else create.mutate();
              }}
            />
          ) : null}

          <ListState
            phase={listPhase(requirements, requirements.data?.length)}
            surface="table"
            rows={3}
            failedMessage="We couldn’t load requirements just now, so this is not a statement that none are configured."
            onRetry={() => void requirements.refetch()}
            empty={
              <SuccessEmpty
                heading="No requirements configured"
                message="Nobody is judged against a supervision requirement until you create one."
              />
            }
          >
            <ul className="cmp-controls">
              {(requirements.data ?? []).map((r) => (
                <li key={r.id} className="cmp-control">
                  <div className="cmp-control-head">
                    <span className="cmp-control-title">
                      {r.name}
                      {r.status === 'retired' ? <span className="rn-muted"> · retired</span> : null}
                    </span>
                    <Pill variant={r.status === 'active' ? 'good' : 'plain'} dot>
                      {CADENCE_LABELS[r.cadence]}
                    </Pill>
                  </div>
                  <p className="rn-muted cmp-control-desc">{r.summary}</p>
                  <p className="cmp-control-detail">
                    In force {formatCalendarDate(r.effectiveFrom)}
                    {r.effectiveTo ? ` through ${formatCalendarDate(r.effectiveTo)}` : ' onward'}
                    {r.appliesToRoleKey ? ` · role: ${r.appliesToRoleKey}` : ''}
                    {r.appliesToEmploymentStatus ? ` · ${r.appliesToEmploymentStatus} workers` : ''}
                  </p>
                  {r.status === 'active' && r.effectiveTo === null ? (
                    <div
                      style={{
                        display: 'flex',
                        gap: 'var(--space-8)',
                        marginTop: 'var(--space-8)',
                      }}
                    >
                      <button
                        type="button"
                        className="rn-btn-quiet"
                        onClick={() => {
                          setEditingId(r.id);
                          setCreating(false);
                          setDraft(draftFromRequirement(r));
                          setFormError(null);
                        }}
                      >
                        Amend
                      </button>
                      {confirmRetireId === r.id ? (
                        <>
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            disabled={retire.isPending}
                            onClick={() => retire.mutate(r.id)}
                          >
                            {retire.isPending ? 'Retiring…' : 'Yes, retire this requirement'}
                          </button>
                          <button
                            type="button"
                            className="rn-btn-quiet"
                            onClick={() => setConfirmRetireId(null)}
                          >
                            Cancel
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="rn-btn-quiet"
                          onClick={() => setConfirmRetireId(r.id)}
                        >
                          Retire
                        </button>
                      )}
                    </div>
                  ) : null}
                </li>
              ))}
            </ul>
          </ListState>
        </SettingsCard>
      ) : null}
    </div>
  );
}

/**
 * A compliance figure. Takes a phase, never a number — so there is no signature
 * through which a caller can hand it a `?? 0`.
 */
function Stat({ label, phase }: { label: string; phase: FigurePhase }) {
  return (
    <div className="cmp-stat">
      <span className="cmp-stat-value" data-state={phase.kind}>
        {figureText(phase, String)}
      </span>
      <span className="cmp-stat-label">{label}</span>
    </div>
  );
}

function RequirementForm({
  draft,
  setDraft,
  errors,
  programOptions,
  programPicker,
  isAmend,
  submitting,
  formError,
  canSubmit,
  onCancel,
  onSubmit,
}: {
  draft: RequirementDraft;
  setDraft: (d: RequirementDraft) => void;
  errors: Record<string, string>;
  programOptions: { id: string; name: string }[];
  /**
   * The picker's four states. An empty `<select>` reads as "this organization
   * has no programs" whether the read was refused, failed, still in flight, or
   * genuinely answered none — and it goes on looking operable while being
   * unusable, which is the defect this codebase ranks worst.
   */
  programPicker: PickerState;
  isAmend: boolean;
  submitting: boolean;
  formError: string | null;
  canSubmit: boolean;
  onCancel: () => void;
  onSubmit: () => void;
}) {
  return (
    <form
      className="org-create"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      {isAmend ? (
        <p className="rn-muted" style={{ fontSize: 13 }}>
          Amending closes the current window the day before this takes effect and opens a new one —
          past periods keep the verdict they already earned. The name cannot change.
        </p>
      ) : null}
      <div className="org-create-grid">
        <SettingsField label="Name" error={errors['name']}>
          <input
            style={{ marginBottom: 0 }}
            value={draft.name}
            disabled={isAmend}
            onChange={(e) => setDraft({ ...draft, name: e.target.value })}
          />
        </SettingsField>
        <SettingsField label="Cadence">
          <select
            style={{ marginBottom: 0 }}
            value={draft.cadence}
            onChange={(e) =>
              setDraft({ ...draft, cadence: e.target.value as typeof draft.cadence })
            }
          >
            <option value="weekly">Weekly</option>
            <option value="biweekly">Every two weeks</option>
            <option value="monthly">Monthly</option>
            <option value="quarterly">Quarterly</option>
          </select>
        </SettingsField>
      </div>
      <div className="org-create-grid">
        <SettingsField label="Minutes required" error={errors['minutesRequired']}>
          <input
            style={{ marginBottom: 0 }}
            type="number"
            min={1}
            max={10080}
            value={draft.minutesRequired}
            onChange={(e) => setDraft({ ...draft, minutesRequired: e.target.value })}
          />
        </SettingsField>
        <SettingsField
          label="Sessions required"
          hint="At least this many separate sessions, in addition to the minutes."
          error={errors['sessionsRequired']}
        >
          <input
            style={{ marginBottom: 0 }}
            type="number"
            min={1}
            max={100}
            value={draft.sessionsRequired}
            onChange={(e) => setDraft({ ...draft, sessionsRequired: e.target.value })}
          />
        </SettingsField>
      </div>
      <SettingsField label="Description (optional)">
        <textarea
          style={{ marginBottom: 0 }}
          rows={2}
          value={draft.description}
          onChange={(e) => setDraft({ ...draft, description: e.target.value })}
        />
      </SettingsField>
      <div className="org-create-grid">
        <SettingsField
          label="Applies to role (optional)"
          hint="A role key, matched exactly — leave blank for everyone."
        >
          <input
            style={{ marginBottom: 0 }}
            value={draft.appliesToRoleKey}
            onChange={(e) => setDraft({ ...draft, appliesToRoleKey: e.target.value })}
          />
        </SettingsField>
        <SettingsField label="Applies to program (optional)">
          <select
            style={{ marginBottom: 0 }}
            value={draft.appliesToProgramId}
            disabled={programPicker.disabled}
            onChange={(e) => setDraft({ ...draft, appliesToProgramId: e.target.value })}
          >
            <option value="">{programPicker.placeholder}</option>
            {programOptions.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>
          {programPicker.note !== null ? (
            <p className="rn-muted" style={{ fontSize: 12 }} role="note">
              {programPicker.note}
            </p>
          ) : null}
        </SettingsField>
      </div>
      <div className="org-create-grid">
        <SettingsField label="Applies to employment status (optional)">
          <select
            style={{ marginBottom: 0 }}
            value={draft.appliesToEmploymentStatus}
            onChange={(e) =>
              setDraft({
                ...draft,
                appliesToEmploymentStatus: e.target.value as typeof draft.appliesToEmploymentStatus,
              })
            }
          >
            <option value="">Any status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="terminated">Terminated</option>
          </select>
        </SettingsField>
        <SettingsField label="Effective from" error={errors['effectiveFrom']}>
          <input
            style={{ marginBottom: 0 }}
            type="date"
            value={draft.effectiveFrom}
            onChange={(e) => setDraft({ ...draft, effectiveFrom: e.target.value })}
          />
        </SettingsField>
      </div>
      <SettingsField
        label="Effective through (optional)"
        hint="Leave blank for a requirement with no planned end."
        error={errors['effectiveTo']}
      >
        <input
          style={{ marginBottom: 0 }}
          type="date"
          value={draft.effectiveTo}
          onChange={(e) => setDraft({ ...draft, effectiveTo: e.target.value })}
        />
      </SettingsField>

      {formError ? (
        <p style={{ color: 'var(--color-urgent-deep)', fontSize: 13 }} role="alert">
          {formError}
        </p>
      ) : null}

      <div className="set-actions">
        <button
          type="submit"
          className="signin-devbtn"
          style={{ width: 'auto', padding: 'var(--space-10) var(--space-16)' }}
          disabled={!canSubmit || submitting}
        >
          {submitting ? 'Saving…' : isAmend ? 'Save change' : 'Create requirement'}
        </button>
        <button type="button" className="rn-btn-quiet" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
